import React, { useEffect, useState } from 'react';
import { ClipPrompt } from '../../api/structs/clip';
import Modal from './Modal';
import NarrationFitPanel from './NarrationFitPanel';
import { Button } from '../ui';
import API from '../../api/api';
import ModelsAPI from '../../api/models';
import {
  AIModel,
  ConstraintField,
  ModelConstraintsResponse,
  usdToCredits,
} from '../../api/structs/model';
import { useToast } from '../../hooks/useToast';
import { useCostDisplay } from '../../hooks/useCostDisplay';

interface NarrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  clipId: string;
  clip: ClipPrompt;
  onSuccess: () => void;
}

interface NarrationAudioOptions {
  voice: string;
  language: string;
  turbo: boolean;
  emotion: string;
  speed: number;
  volume: number;
  pitch: number;
  language_boost: string;
}

type ConstraintFieldMap = Record<string, ConstraintField>;

const DEFAULT_AUDIO_OPTIONS: NarrationAudioOptions = {
  voice: '',
  language: '',
  turbo: false,
  emotion: '',
  speed: 1,
  volume: 1,
  pitch: 0,
  language_boost: '',
};

const selectCls = 'w-full bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30';
const labelCls = 'block text-xs text-slate-400 mb-1';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const asStringEnum = (field?: ConstraintField): string[] =>
  isStringArray(field?.enum) ? field!.enum.map((item) => String(item)) : [];

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const asBooleanDefault = (field?: ConstraintField, fallback = false): boolean =>
  typeof field?.default === 'boolean' ? field.default : fallback;

const asNumberDefault = (field: ConstraintField | undefined, fallback: number): number => {
  const value = asNumber(field?.default);
  return value ?? fallback;
};

const pickDefaultString = (field: ConstraintField | undefined, values: string[]): string => {
  if (typeof field?.default === 'string') return field.default;
  return values[0] ?? '';
};

const buildDefaultAudioOptions = (fields: ConstraintFieldMap): NarrationAudioOptions => {
  const voiceOptions = asStringEnum(fields.voice);
  const languageOptions = asStringEnum(fields.language);
  const languageBoostOptions = asStringEnum(fields.language_boost);

  return {
    voice: pickDefaultString(fields.voice, voiceOptions),
    language: pickDefaultString(fields.language, languageOptions),
    turbo: asBooleanDefault(fields.turbo, false),
    emotion: typeof fields.emotion?.default === 'string' ? fields.emotion.default : '',
    speed: asNumberDefault(fields.speed, 1),
    volume: asNumberDefault(fields.volume, 1),
    pitch: asNumberDefault(fields.pitch, 0),
    language_boost: pickDefaultString(fields.language_boost, languageBoostOptions),
  };
};

const formatVoiceLabel = (value: string): string => {
  if (/^[a-z]+$/.test(value)) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
};

const formatLanguageLabel = (code: string): string => {
  if (code === 'auto') return 'Auto';

  const [languageCode, regionCode] = code.split('-');
  const intlWithDisplayNames = Intl as typeof Intl & {
    DisplayNames?: new (
      locales?: string | string[],
      options?: { type: 'language' | 'region' }
    ) => { of: (value: string) => string | undefined };
  };
  const languageNames = intlWithDisplayNames.DisplayNames
    ? new intlWithDisplayNames.DisplayNames(['en'], { type: 'language' })
    : null;
  const regionNames = intlWithDisplayNames.DisplayNames
    ? new intlWithDisplayNames.DisplayNames(['en'], { type: 'region' })
    : null;

  const languageLabel = languageNames?.of(languageCode) ?? languageCode;
  if (!regionCode) return languageLabel;

  const regionLabel = regionNames?.of(regionCode.toUpperCase()) ?? regionCode.toUpperCase();
  return `${languageLabel} (${regionLabel})`;
};

const formatCreditsPer1k = (value: unknown): string | null => {
  const amount = asNumber(value);
  if (amount === undefined) return null;
  const credits = usdToCredits(amount);
  return `⚡ ${credits.toLocaleString()}/1k`;
};

const getFeaturedVoiceGroups = (
  voices: string[],
  prefixes: string[]
): { featured: string[]; other: string[] } => {
  if (prefixes.length === 0) return { featured: [], other: voices };
  return {
    featured: voices.filter((voice) => prefixes.some((prefix) => voice.startsWith(prefix))),
    other: voices.filter((voice) => !prefixes.some((prefix) => voice.startsWith(prefix))),
  };
};

const NarrationModal: React.FC<NarrationModalProps> = ({
  isOpen,
  onClose,
  clipId,
  clip,
  onSuccess,
}) => {
  const toast = useToast();
  const display = useCostDisplay();
  const [models, setModels] = useState<AIModel[]>([]);
  const [modelId, setModelId] = useState('');
  const [text, setText] = useState('');
  const [constraints, setConstraints] = useState<ModelConstraintsResponse | null>(null);
  const [audioOptions, setAudioOptions] = useState<NarrationAudioOptions>(DEFAULT_AUDIO_OPTIONS);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingConstraints, setIsLoadingConstraints] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setText(typeof clip.metadata?.subtitleText === 'string' ? clip.metadata.subtitleText : '');
    setIsLoadingModels(true);

    ModelsAPI.getNarratorModels()
      .then((list) => {
        setModels(list);
        setModelId((current) => {
          if (current && list.some((model) => model.id === current)) return current;
          return list[0]?.id ?? '';
        });
      })
      .catch(() => {
        setModels([]);
        setModelId('');
      })
      .finally(() => setIsLoadingModels(false));
  }, [clip.metadata?.subtitleText, isOpen]);

  useEffect(() => {
    if (!isOpen || !modelId) {
      setConstraints(null);
      setAudioOptions(DEFAULT_AUDIO_OPTIONS);
      return;
    }

    let cancelled = false;
    setIsLoadingConstraints(true);

    ModelsAPI.getModelConstraints(modelId, 'audio')
      .then((response) => {
        if (cancelled) return;
        setConstraints(response);
        setAudioOptions(buildDefaultAudioOptions((response.fields ?? {}) as ConstraintFieldMap));
      })
      .catch(() => {
        if (cancelled) return;
        setConstraints({
          model_id: modelId,
          fields: {},
          capabilities: {},
        });
        setAudioOptions(DEFAULT_AUDIO_OPTIONS);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingConstraints(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, modelId]);

  const audioFields = ((constraints?.fields ?? {}) as ConstraintFieldMap);
  const audioCapabilities = (constraints?.capabilities ?? {}) as Record<string, unknown>;

  const voiceField = audioFields.voice;
  const languageField = audioFields.language;
  const turboField = audioFields.turbo;
  const emotionField = audioFields.emotion;
  const speedField = audioFields.speed;
  const volumeField = audioFields.volume;
  const pitchField = audioFields.pitch;
  const languageBoostField = audioFields.language_boost;

  const voiceOptions = asStringEnum(voiceField);
  const languageOptions = asStringEnum(languageField);
  const emotionOptions = asStringEnum(emotionField);
  const languageBoostOptions = asStringEnum(languageBoostField);
  const featuredVoicePrefixes = isStringArray(audioCapabilities.featured_voice_prefixes)
    ? audioCapabilities.featured_voice_prefixes
    : [];
  const { featured: featuredVoices, other: otherVoices } = getFeaturedVoiceGroups(
    voiceOptions,
    featuredVoicePrefixes
  );
  const supportsTurbo = turboField?.type === 'boolean' || audioCapabilities.supports_turbo === true;
  const hdPrice = formatCreditsPer1k(audioCapabilities.pricing_hd_per_1k_chars);
  const turboPrice = formatCreditsPer1k(audioCapabilities.pricing_turbo_per_1k_chars);

  const selectedModel = models.find((m) => m.id === modelId) ?? null;
  // One provider key for everything this modal does: the generate call stamps
  // it on the take, and the budget is measured per (provider, model, voice,
  // speed). Two different strings would look the rate up where it was never
  // stored, and "measured" would degrade to "default rate" for good.
  const provider = selectedModel?.provider || 'runware';
  const narrationCostCredits = (() => {
    const per1k = selectedModel?.pricing?.per_1k_chars;
    if (!per1k) return null;
    const pricePerChar = parseFloat(per1k) / 1000;
    const charCount = Math.max(text.trim().length, 1);
    return usdToCredits(pricePerChar * charCount);
  })();
  const hasVoiceChoice = voiceOptions.length > 0;

  /**
   * `speedOverride` is "Fit to clip": the same generate call, at the speed that
   * lands the take on the clip. An override is always sent, even when it equals
   * the model's default, because it is the whole point of the regeneration.
   */
  const handleSubmit = async (speedOverride?: number) => {
    // A second click while a take is generating would spend the credits twice
    // and race two `narrator_media_id` writes.
    if (isSubmitting) return;
    if (!text.trim()) {
      toast({ text: 'Enter narration text', level: 'error' });
      return;
    }
    if (hasVoiceChoice && !audioOptions.voice) {
      toast({ text: 'Narration options are still loading', level: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      const audioSpec: Record<string, unknown> = {};

      if (audioOptions.voice) {
        audioSpec.voice = audioOptions.voice;
      }
      if (audioOptions.language) {
        audioSpec.language = audioOptions.language;
      }
      if (supportsTurbo) {
        audioSpec.turbo = audioOptions.turbo;
      }
      if (!audioOptions.turbo && audioOptions.emotion) {
        audioSpec.emotion = audioOptions.emotion;
      }
      const speed = speedOverride ?? audioOptions.speed;
      if (
        speedField &&
        (speedOverride !== undefined || speed !== asNumberDefault(speedField, 1))
      ) {
        audioSpec.speed = speed;
      }
      if (
        volumeField &&
        audioOptions.volume !== asNumberDefault(volumeField, 1)
      ) {
        audioSpec.volume = audioOptions.volume;
      }
      if (
        pitchField &&
        audioOptions.pitch !== asNumberDefault(pitchField, 0)
      ) {
        audioSpec.pitch = audioOptions.pitch;
      }
      if (
        languageBoostField &&
        audioOptions.language_boost &&
        audioOptions.language_boost !== pickDefaultString(languageBoostField, languageBoostOptions)
      ) {
        audioSpec.language_boost = audioOptions.language_boost;
      }

      const mediaId = await API.createMediaItem({
        clip_id: clipId,
        type: 'audio',
        generator: provider,
        prompt: text.trim(),
        metadata: { role: 'narrator' },
        output_spec: {
          provider,
          model: modelId,
          audio: audioSpec,
        } as any,
      });
      await API.editClipMetadata(clipId, 'narrator_media_id', mediaId);
      toast({ text: 'Narration generating - audio will appear when ready', level: 'success' });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({ text: `Narration failed: ${error.message}`, level: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Narration" size="md">
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Narration text</label>
          <textarea
            aria-label="Narration text"
            className="w-full bg-slate-800 text-white text-sm rounded-md px-3 py-2 border border-white/10 focus:outline-none focus:border-white/30 resize-none"
            rows={4}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Enter the text to be spoken..."
            autoFocus
          />
        </div>

        <NarrationFitPanel
          clip={clip}
          provider={provider}
          modelId={modelId}
          voice={audioOptions.voice}
          speed={audioOptions.speed}
          canSetSpeed={Boolean(speedField)}
          isReady={!isLoadingModels && !isLoadingConstraints}
          isSubmitting={isSubmitting}
          text={text}
          onUseText={setText}
          onFit={handleSubmit}
        />

        <div>
          <label className={labelCls}>Model</label>
          <select
            aria-label="Model"
            className={selectCls}
            value={modelId}
            onChange={(event) => setModelId(event.target.value)}
            disabled={isLoadingModels || models.length === 0}
          >
            {models.length === 0 ? (
              <option value="">
                {isLoadingModels ? 'Loading narrator models...' : 'No narrator models available'}
              </option>
            ) : (
              models.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))
            )}
          </select>
        </div>

        {voiceOptions.length > 0 && (
          <div>
            <label className={labelCls}>Voice</label>
            <select
              aria-label="Voice"
              className={selectCls}
              value={audioOptions.voice}
              onChange={(event) =>
                setAudioOptions((current) => ({ ...current, voice: event.target.value }))
              }
              disabled={isLoadingConstraints}
            >
              {featuredVoices.length > 0 && otherVoices.length > 0 ? (
                <>
                  <optgroup label="Featured">
                    {featuredVoices.map((voice) => (
                      <option key={voice} value={voice}>
                        {formatVoiceLabel(voice)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="All voices">
                    {otherVoices.map((voice) => (
                      <option key={voice} value={voice}>
                        {formatVoiceLabel(voice)}
                      </option>
                    ))}
                  </optgroup>
                </>
              ) : (
                voiceOptions.map((voice) => (
                  <option key={voice} value={voice}>
                    {formatVoiceLabel(voice)}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {(languageOptions.length > 0 || supportsTurbo || languageBoostOptions.length > 0) && (
          <div className="flex gap-3">
            {languageOptions.length > 0 && (
              <div className="flex-1">
                <label className={labelCls}>Language</label>
                <select
                  aria-label="Language"
                  className={selectCls}
                  value={audioOptions.language}
                  onChange={(event) =>
                    setAudioOptions((current) => ({ ...current, language: event.target.value }))
                  }
                  disabled={isLoadingConstraints}
                >
                  {languageOptions.map((language) => (
                    <option key={language} value={language}>
                      {formatLanguageLabel(language)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {supportsTurbo && (
              <div className="flex-1">
                <label className={labelCls}>Quality</label>
                <div className="flex gap-1">
                  {([
                    {
                      label: 'HD',
                      price: hdPrice,
                      value: false,
                    },
                    {
                      label: 'Turbo',
                      price: turboPrice,
                      value: true,
                    },
                  ] as const).map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() =>
                        setAudioOptions((current) => ({
                          ...current,
                          turbo: option.value,
                          emotion: option.value ? '' : current.emotion,
                        }))
                      }
                      className={`flex-1 py-1.5 text-xs rounded-md border transition-colors ${
                        audioOptions.turbo === option.value
                          ? 'bg-white/10 border-white/20 text-white'
                          : 'border-white/10 text-slate-400 hover:text-white'
                      }`}
                    >
                      {option.label}
                      {option.price ? ` (${option.price})` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {languageBoostOptions.length > 0 && (
              <div className="flex-1">
                <label className={labelCls}>Language Boost</label>
                <select
                  aria-label="Language Boost"
                  className={selectCls}
                  value={audioOptions.language_boost}
                  onChange={(event) =>
                    setAudioOptions((current) => ({
                      ...current,
                      language_boost: event.target.value,
                    }))
                  }
                  disabled={isLoadingConstraints}
                >
                  {languageBoostOptions.map((language) => (
                    <option key={language} value={language}>
                      {formatLanguageLabel(language)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {emotionOptions.length > 0 && !audioOptions.turbo && (
          <div>
            <label className={labelCls}>Emotion</label>
            <select
              aria-label="Emotion"
              className={selectCls}
              value={audioOptions.emotion}
              onChange={(event) =>
                setAudioOptions((current) => ({ ...current, emotion: event.target.value }))
              }
              disabled={isLoadingConstraints}
            >
              <option value="">None</option>
              {emotionOptions.map((emotion) => (
                <option key={emotion} value={emotion}>
                  {emotion}
                </option>
              ))}
            </select>
          </div>
        )}

        {(speedField || volumeField || pitchField) && (
          <div className="grid grid-cols-3 gap-3">
            {speedField && (
              <div>
                <label className={labelCls}>Speed ({audioOptions.speed.toFixed(1)}x)</label>
                <input
                  type="range"
                  min={asNumber(speedField.min) ?? 0.5}
                  max={asNumber(speedField.max) ?? 2}
                  step={0.1}
                  value={audioOptions.speed}
                  onChange={(event) =>
                    setAudioOptions((current) => ({
                      ...current,
                      speed: parseFloat(event.target.value),
                    }))
                  }
                  className="w-full accent-white"
                />
              </div>
            )}

            {volumeField && (
              <div>
                <label className={labelCls}>Volume ({audioOptions.volume.toFixed(1)})</label>
                <input
                  type="range"
                  min={asNumber(volumeField.min) ?? 0.1}
                  max={asNumber(volumeField.max) ?? 10}
                  step={0.1}
                  value={audioOptions.volume}
                  onChange={(event) =>
                    setAudioOptions((current) => ({
                      ...current,
                      volume: parseFloat(event.target.value),
                    }))
                  }
                  className="w-full accent-white"
                />
              </div>
            )}

            {pitchField && (
              <div>
                <label className={labelCls}>
                  Pitch ({audioOptions.pitch > 0 ? '+' : ''}{audioOptions.pitch})
                </label>
                <input
                  type="range"
                  min={asNumber(pitchField.min) ?? -12}
                  max={asNumber(pitchField.max) ?? 12}
                  step={1}
                  value={audioOptions.pitch}
                  onChange={(event) =>
                    setAudioOptions((current) => ({
                      ...current,
                      pitch: parseInt(event.target.value, 10),
                    }))
                  }
                  className="w-full accent-white"
                />
              </div>
            )}
          </div>
        )}

        <p className="text-[10px] text-zinc-600 font-mono">
          narrator audio layers over music - music ducked to 30% volume
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-white/10 mt-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleSubmit()}
          disabled={isSubmitting || isLoadingModels || isLoadingConstraints || !modelId}
        >
          <span className="flex items-center gap-1.5">
            {isSubmitting ? 'Generating...' : 'Generate Narration'}
            {!isSubmitting && display === 'credits' && narrationCostCredits != null && (
              <span className="text-yellow-300 font-bold">⚡ ~{narrationCostCredits.toLocaleString()}</span>
            )}
          </span>
        </Button>
      </div>
    </Modal>
  );
};

export default NarrationModal;
